import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchCustomerMachineDetails } from '../../helper/CustomerMachineDetailsHelper';
import './HospitalDropdown.css';

const joinAddressParts = (hospital) => (
  [hospital?.Address, hospital?.Address2, hospital?.City, hospital?.PostCode]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ')
);

const HospitalDropdown = ({ value, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    fetchCustomerMachineDetails()
      .then((data) => {
        if (!cancelled) setHospitals(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load hospitals.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    searchRef.current?.focus();

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const selectedHospital = useMemo(
    () => hospitals.find((hospital) => hospital.CustomerNo === value) || null,
    [hospitals, value]
  );

  const filteredHospitals = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return hospitals;
    return hospitals.filter((hospital) => String(hospital.Name || '').toLowerCase().includes(q));
  }, [hospitals, query]);

  const selectHospital = (hospital) => {
    onChange?.(hospital || null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`hospital-dropdown${open ? ' hospital-dropdown--open' : ''}${value ? ' hospital-dropdown--active' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        className="hospital-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by hospital"
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span className="hospital-dropdown__meta">
          <span className="hospital-dropdown__label">Hospital</span>
          <span className="hospital-dropdown__value">
            {selectedHospital?.Name || (value ? 'Selected hospital' : 'All Hospitals')}
          </span>
        </span>
        <svg className="hospital-dropdown__chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5 7.5 10 12.5 15 7.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="hospital-dropdown__menu" role="listbox">
          <div className="hospital-dropdown__search">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search hospital name..."
              aria-label="Search hospital name"
            />
          </div>
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={`hospital-dropdown__option${!value ? ' hospital-dropdown__option--selected' : ''}`}
            onClick={() => selectHospital(null)}
          >
            <span className="hospital-dropdown__option-name">All Hospitals</span>
          </button>
          {loading && <div className="hospital-dropdown__empty">Loading hospitals…</div>}
          {!loading && error && <div className="hospital-dropdown__empty">{error}</div>}
          {!loading && !error && filteredHospitals.length === 0 && (
            <div className="hospital-dropdown__empty">No hospitals match</div>
          )}
          {!loading && !error && filteredHospitals.map((hospital) => {
            const address = joinAddressParts(hospital);
            return (
              <button
                key={hospital.CustomerNo || hospital.Name}
                type="button"
                role="option"
                aria-selected={value === hospital.CustomerNo}
                className={`hospital-dropdown__option${value === hospital.CustomerNo ? ' hospital-dropdown__option--selected' : ''}`}
                onClick={() => selectHospital(hospital)}
              >
                <span className="hospital-dropdown__option-name">{hospital.Name || '—'}</span>
                {address && <span className="hospital-dropdown__option-address">{address}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HospitalDropdown;
